import {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import useLabels from 'infrastructure/js/hooks/useLabels';
import List from 'infrastructure/js/components/List/list';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import {enumTypes, getEnumValue} from '../../../../../../utils/enumHelper';
import Checkbox from 'infrastructure/js/components/controls/Checkbox/checkbox';
import Button from 'infrastructure/js/components/controls/Button/button';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import comparisonOperatorsEnum from '../../../../../../enums/comparisonOperators';
import {
  attachmentTypeOptions,
  choiceListOptions,
  frequencyOptions, frequencyTypesOptions,
  qplTestPassCriteriaOptions
} from '../../../utils/qplHelper';
import RemoveTabIcon from 'infrastructure/assets/svg/remove-tab.svg';
import RangeListItem, { checkConflict } from '../../../../../Common/Controls/AddRemoveList/AddRemoveListItem/RangeListItem/rangeListItem';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import {unitTypes} from 'infrastructure/js/utils/uomHelper';
import withPortal from 'infrastructure/js/components/HOCs/withPortal/withPortal';
import { PL_NoFormComboBox } from 'infrastructure/js/components/controls/Combobox/combobox.js';

import './qplTestListItem.scss';

function getIndexFromPath(path, fieldName) {
  const match = path.match(new RegExp(`\\[${fieldName}\\]\\[(\\d+)\\]`));
  return match ? parseInt(match[1], 10) : null;
}

function isShowPredefinedValues(testFields, index) {
  const found =  testFields?.[index]?.find(item => (item.type === 'CHOICE_LIST' && item.showPredefinedValues === true))
  return !!found;
}

function isPassFail(testFields, index) {
  const found =  testFields?.[index]?.find(item => (item.type === 'CHOICE_LIST' && item.isPassFail === true))
  return !!found;
}

function isShowPerMaterialQuantity(testFields, index) {
  const found =  testFields?.[index]?.find(item => (item.type === 'FREQUENCY' && item.showPerMaterialQuantity === true))
  return !!found;
}

const ComboboxPortal = withPortal(Combobox);

const NoFormComboboxPortal = withPortal(PL_NoFormComboBox);


const QplTestListItem = ({ name,
                           testFields = [],
                           optionalFields = [],
                           index,
                           listName,
                           change,
                           onFieldAdd = null,
                           onFieldRemove = null,
                           onFieldChange = null,
}) => {

  const labels = useLabels('mat.forms.qplTestForm.quantityTestListItem.');
  const allLabels = useLabels('');
  const validationLabels = useLabels('mat.validation.messages.');

  const [showOptionalFieldsMenu, setShowOptionalFieldsMenu] = useState(false);
  const [updateKey, setUpdateKey] = useState(0);
  const [frequencyUpdateKey, setFrequencyUpdateKey] = useState(0);


  const majorUOMType = PermissionManager.isWeightSupported() ? unitTypes.WEIGHT : unitTypes.LENGTH;


  const validateChoiceListRangeConflict = useCallback((value, allValues, props, name) => {

    const rowIndex = getIndexFromPath(name, 'choiceList');
    if (rowIndex === null) {
      return;
    }
    const currentRow = allValues?.[listName]?.[index]?.choiceList?.[rowIndex];
    if (!currentRow?.operator?.value || value === undefined || value === null) {
      return;
    }

    const { operator, minQuantity, quantity } = currentRow;
    const minQuantityVal = parseInt(minQuantity);
    const quantityVal = parseInt(quantity);

    if(minQuantityVal > quantityVal) {
      return validationLabels.get('minGreaterThanMax');
    }

    let error;

    allValues?.[listName]?.[index]?.choiceList?.forEach((otherRow, otherIndex) => {
      if (rowIndex !== otherIndex  )  {
        const { operator: otherOperator, minQuantity: otherMinQuantity, quantity: otherQuantity } = otherRow;
        const otherMinQuantityVal = parseInt(otherMinQuantity);
        const otherQuantityVal = parseInt(otherQuantity);

        if (!otherOperator?.value || isNaN(otherQuantityVal) || (otherOperator?.value === comparisonOperatorsEnum.BETWEEN && isNaN(otherMinQuantityVal))) {
          return; // Skip invalid rows
        }

        const conflict = checkConflict(
            { operator: operator?.value, minQuantity: minQuantityVal, quantity: quantityVal },
            { operator: otherOperator?.value, minQuantity: otherMinQuantityVal, quantity: otherQuantityVal }
        );

        if (conflict) {
          error = validationLabels.get('rangeConflict');
        }
      }
    });

    return error;
  }, [ index, validationLabels]);

  const validateFrequencyRangeConflict = useCallback((value, allValues, props, name) => {

    const rowIndex = getIndexFromPath(name, 'frequency');
    if (rowIndex === null) {
      return;
    }
    const currentRow = allValues?.[listName]?.[index]?.frequency?.[rowIndex];
    if (!currentRow?.operator?.value || value === undefined || value === null) {
      return;
    }

    const { operator, minQuantity, quantity } = currentRow;
    const minQuantityVal = parseInt(minQuantity);
    const quantityVal = parseInt(quantity);

    if(minQuantityVal > quantityVal) {
      return validationLabels.get('minGreaterThanMax');
    }

    let error;

    allValues?.[listName]?.[index]?.frequency?.forEach((otherRow, otherIndex) => {
      if (rowIndex !== otherIndex  )  {
        const { operator: otherOperator, minQuantity: otherMinQuantity, quantity: otherQuantity } = otherRow;
        const otherMinQuantityVal = parseInt(otherMinQuantity);
        const otherQuantityVal = parseInt(otherQuantity);

        if (!otherOperator?.value || isNaN(otherQuantityVal) || (otherOperator?.value === comparisonOperatorsEnum.BETWEEN && isNaN(otherMinQuantityVal))) {
          return; // Skip invalid rows
        }

        const conflict = checkConflict(
            { operator: operator?.value, minQuantity: minQuantityVal, quantity: quantityVal },
            { operator: otherOperator?.value, minQuantity: otherMinQuantityVal, quantity: otherQuantityVal }
        );

        if (conflict) {
          error = validationLabels.get('rangeConflict');
        }
      }
    });

    return error;
  }, [ index, validationLabels]);

//----------- Frequency field---------
  const onFrequencyChange = (newValue, oldValue) => {
    if (newValue && oldValue && newValue.value === oldValue.value ) {
      return;
    }
    const isPerMaterialQuantity = newValue?.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('PER_MATERIAL_QUANTITY');
    change(name + '.[frequency]', isPerMaterialQuantity ? [{}] : null);

    onFieldChange?.(index, 'FREQUENCY', isPerMaterialQuantity);
  }

  const onFrequencyTypeChange = (newValue, oldValue, reduxName) => {
    if (newValue && oldValue && newValue.value === oldValue.value ) {
      return;
    }
    change(reduxName + '.[frequencyInterval]', null);
    setFrequencyUpdateKey(frequencyUpdateKey => 1 - frequencyUpdateKey);
  }

  const renderFrequencyField = (item, label, fieldIndex) => {
    return (
      <>
        <InputSection key={item.id + '-' + fieldIndex} label={label}  className="choice-list-section">
          <ComboboxPortal id={name + '.[frequencySelect]'}
                          name={name + '.[frequencySelect]'}
                          options={frequencyOptions()}
                          onChangeCallback={onFrequencyChange}
                          validate={Validation.dropdown.required}
          />
        </InputSection>
        {!!isShowPerMaterialQuantity(testFields, index) &&
          <InputSection key={item.id + '-pv-' + fieldIndex} label={labels.get('frequencyPerMaterial') + '*'}  className="list-section">
            {renderFrequencyPerMaterialList()}
          </InputSection>}
      </>
    )
  }

  const getFrequencyPerMaterialItemRenderer = (data) => {
    const isInterval = (data?.fieldData?.frequencyType?.value === getEnumValue(enumTypes.FREQUENCY_VALUE)('INTERVAL_RAW_MATERIAL'));

    return (

      <RangeListItem
        {...data}
        unitType={majorUOMType}
        change={change}
        validate={[Validation.number.required, validateFrequencyRangeConflict]}
      >
        {({ name }) => (
          <>
            <ComboboxPortal
              id={name + '.[frequencyType]'}
              name={name + '.[frequencyType]'}
              options={frequencyTypesOptions()}
              className='frequency-type'
              placeholder={labels.get('frequencyType')}
              onChangeCallback={(a,b) => onFrequencyTypeChange(a, b, name)}
              validate={Validation.dropdown.required}
            />
            {isInterval && <TextField
              id={name + '.[frequencyInterval]'}
              name={name + '.[frequencyInterval]'}
              className="short-textfield"
              placeholder={'Interval'}
              normalize={Normalize.number(true, 1, 9999)}
              validate={Validation.required}
            />}
          </>
        )}
      </RangeListItem>
    )
  };

  const renderFrequencyPerMaterialList = () => {
    return (
      <List
        updateKey={frequencyUpdateKey}
        id={name + '.[frequency]'}
        name={name + '.[frequency]'}
        className='frequency-values-list values-list show-controls-on-hover'
        itemRenderer={getFrequencyPerMaterialItemRenderer}
        minFieldsCount={1}
      />
    )
  }

  //----------- Choice List field---------
  const onChoiceListChange = (newValue, oldValue) => {
    if (newValue && oldValue && newValue.value === oldValue.value ) {
      return;
    }

    const isPredefinedValues = newValue?.value === getEnumValue(enumTypes.CHOICE_LIST_VALUE)('PREDEFINED_VALUE');
    change(name + '.[choiceList]', isPredefinedValues ?  [{}] : null);

    onFieldChange?.(index, 'CHOICE_LIST', newValue?.value);

    setUpdateKey(updateKey => 1 - updateKey)
  }

  const renderChoiceListField = (item, label, fieldIndex) => {
    return (
      <>
        <InputSection key={item.id + '-' + fieldIndex} label={label}  className="choice-list-section">
          <ComboboxPortal id={name + '.[choiceListSelect]'}
                    name={name + '.[choiceListSelect]'}
                    options={choiceListOptions()}
                    onChangeCallback={onChoiceListChange}
                    validate={Validation.dropdown.required}
          />
        </InputSection>
        {!!isShowPredefinedValues(testFields, index) &&
          <InputSection key={item.id + '-pv-' + fieldIndex} label={labels.get('predefinedValues') + '*'}  className="list-section">
            {renderPredefinedValuesList()}
          </InputSection>}
      </>
    )
  }

  const getPredefinedValuesItemRenderer = (data) => {
    return (

      <RangeListItem
        {...data}
        unitType={majorUOMType}
        change={change}
        validate={[Validation.number.required, validateChoiceListRangeConflict]}
        isRangeOperatorOnly={true}
      >
      </RangeListItem>
    )
  };

  const renderPredefinedValuesList = () => {
    return (
      <List
        id={name + '.[choiceList]'}
        name={name + '.[choiceList]'}
        className='predefined-values-list values-list'
        itemRenderer={getPredefinedValuesItemRenderer}
        minFieldsCount={1}
        isViewOnly={true}
      />
    )
  }

  //----------- Attachments List field---------
  const getAttachmentItemRenderer = (data) => {
    return (<QplTestAttachmentListItem {...data} change={change}
                                       isMandatory={isPassFail(testFields, index)}/>
    )
  };

  const renderAttachmentsField = (item, label, fieldIndex) => {
    const labelStr = label + (isPassFail(testFields, index) ? ' *' : '');
    return (
      <InputSection key={`${item.id}-${index}-${fieldIndex}`} label={labelStr}
                    className="list-section">
        <List
          updateKey={updateKey}
          id={name + `.[${item.name}]`}
          name={name + `.[${item.name}]`}
          className='attachments-list show-controls-on-hover'
          itemRenderer={getAttachmentItemRenderer}
          minFieldsCount={1}
          maxItemsToRender={4}
        />
      </InputSection>
    )
  }

  //----------- Pass Criteria field---------
  const renderPassCriteriaField = (item, label, index) => {
    return (
      <InputSection key={item.id + '-' + index} label={label} htmlFor={name + `.[${item.name}]`}>
        <ComboboxPortal
          id={name + `.[${item.name}]`}
          name={name + `.[${item.name}]`}
          options={qplTestPassCriteriaOptions()}
          validate={Validation.dropdown.required}
        />

      </InputSection>
    )
  }

  //----------- common Test field---------

  const renderBooleanField = (item, label, index) => {
    const fieldName = `${name}.[${item.name}]`
    return (
      <InputSection key={item.id + '-' + index} label={label} htmlFor={fieldName}>
        <Checkbox id={fieldName} name={fieldName} label={''}/>
      </InputSection>
    );
  }

  const renderCommonField = (item, label, index) => {

    const fieldName = `${name}.[${item.name}]`

    return (
      <InputSection key={item.id + '-' + index} label={label} htmlFor={fieldName}>
        <TextField
          id={fieldName} name={fieldName}
          className="short-textfield"
          maxLength={item.type === 'TEXT' ? 100 : undefined}
          normalize={item.type === 'DECIMAL' ? Normalize.normalizeFloat( 0, 9999.99999, 5) :
            (item.type === 'INTEGER') ? Normalize.number( true, (item.name === 'numOfRepetitions' ? 1 : 0), Number.MAX_SAFE_INTEGER) : undefined}
          validate={item.mandatory ? Validation.required : undefined}
        />
        {renderRemoveFieldButton(item)}
      </InputSection>
    );
  }

  //----------- renderers and handlers---------
  const onMenuItemClick = (item) => {
    setShowOptionalFieldsMenu(false);
    onFieldAdd?.(item?.data, index);
  }

  const renderRemoveFieldButton = (item)  => {
    if (!item.default) {
      return (
        <span className="bag-title-remove-btn" onClick={(e) => onFieldRemoveHandler(item, e)}>
          <RemoveTabIcon className='bag-title-remove-icon'/>
        </span>);
    }
    return null;
  }

  const onFieldRemoveHandler = (item) => {
    onFieldRemove?.(item, index);
  }

  const getAvailableOptionalFields = () => {
    const ids = testFields[index]?.map(item => item.id) ?? [];
    // return optionalFields.map(item => {   //make the item disabled
    //   item.isDisabled = ids.includes(item.value);
    //   return item;
    // });
    return optionalFields?.filter(item => !ids.includes(item.value));
  }

  const renderAddFieldButton = () => {
    return (
      <div className="optional-fields-container">
        <Button id="add-field-btn"
                className="add-field-btn"
                onClick={() => setShowOptionalFieldsMenu(true)}
        >
          <span className="pl pl-icon-add"/> <span>{'Add Field'}</span>
        </Button>

        <NoFormComboboxPortal
          id='optionalFieldsMenu'
          className='optional-fields-menu'
          tabIndex='-1'
          options={showOptionalFieldsMenu ? getAvailableOptionalFields() : []}
          menuIsOpen={showOptionalFieldsMenu}
          maxMenuHeight={210}
          onMenuOpen={() => setShowOptionalFieldsMenu(true)}
          onMenuClose={() => setShowOptionalFieldsMenu(false)}
          onChangeCallback={onMenuItemClick}
          onKeyDown={(e) => e.preventDefault()}  // prevent keyboard input
          components={{
            IndicatorsContainer: () => null,
          }}
        />

      </div>
    )
  }

  const renderTestFields = () => {
    return testFields?.[index]?.map((item, index) => {

      const label = item.custom ? item.displayName : allLabels.get(item.labelKey) + (item.mandatory ? ' *' : '');

      if ( item.type === 'CHOICE_LIST' ) {
        return renderChoiceListField(item, label, index);
      }
      else if (item.type === 'ATTACHMENT') {
        return renderAttachmentsField(item, label, index);
      }
      else if (item.type === 'PASS_CRITERIA') {
        return renderPassCriteriaField(item, label, index);
      }
      else if ( item.type === 'FREQUENCY' ) {
        return renderFrequencyField(item, label, index);
      }
      else if ( item.type === 'BOOLEAN' ) {
        return renderBooleanField(item, label, index);
      }
      return renderCommonField(item, label, index);
    })
  }

  return (
    <div className="qpl-test-list-item">
      {renderTestFields()}
      {renderAddFieldButton()}
    </div>
  );
};

QplTestListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  listName: PropTypes.string.isRequired,
  testFields: PropTypes.array,
  optionalFields: PropTypes.array,
  change: PropTypes.func.isRequired,
  onFieldAdd: PropTypes.func,
  onFieldRemove: PropTypes.func,
  onFieldChange: PropTypes.func,
};


export default QplTestListItem;


const QplTestAttachmentListItem = ({ name, change, isMandatory=false,
                                     index,
                                     allFieldsData,
                         }) => {

  const [isTypeSelected, setIsTypeSelected] = useState(false);

  const onAttachmentTypeChangeCallback = (value) => {
    if (!value) {
      change(name + '.[attachmentCount]', null);
    }
    setIsTypeSelected(!!value);
  }

  const typesInUse = allFieldsData?.map(item => item?.attachmentType?.value) ?? []
  const optionsAllowed = attachmentTypeOptions().filter( (item) => !typesInUse?.includes(item.value) || item.value === typesInUse?.[index])

  return (
    <div className="attachments-list-item">
      <ComboboxPortal
        id={name + '.[attachmentType]'}
        name={name + '.[attachmentType]'}
        options={optionsAllowed}
        onChangeCallback={onAttachmentTypeChangeCallback}
        validate={isMandatory ? Validation.dropdown.required : undefined}
      />
      <TextField
        id={name + '.[attachmentCount]'}
        name={name + '.[attachmentCount]'}
        normalize={Normalize.number(true, 1, 99999)}
        validate={(isTypeSelected || isMandatory) ? Validation.required : undefined}
      />
    </div>
  )
}
